import { Search } from 'lucide-react';
import { useState } from 'react';
import { usePlayer } from '../player';
import { pickerSubtitle } from '../picker';
import { usePlayerSearch, useRooms } from '../queries';
import { themeFor } from '../theme';
import { CenteredScreen } from './CenteredScreen';
import { RoleSwitch } from './RoleSwitch';

/**
 * Staff-facing player lookup. The server scopes results to the rooms the caller operates and
 * caps the page size (PlayerSearch:DefaultLimit, 20 by default), so this is a search rather than
 * a dropdown — a busy platform has far too many players to list.
 */
export function PlayerPicker({ onClose }: { onClose?: () => void }) {
  const { selectPlayer } = usePlayer();
  const [query, setQuery] = useState('');
  const search = usePlayerSearch(query, true);
  // The directory PlayerProvider already reads: each row names its room, since one card can be two players (#528).
  const rooms = useRooms().data ?? [];
  const theme = themeFor(null);

  const results = search.data ?? [];

  return (
    <CenteredScreen background={theme.bg} color={theme.text}>
      <div
        className="w-full max-w-lg rounded-2xl p-6"
        style={{ background: theme.panel, border: `1px solid ${theme.border}` }}
      >
        <h1 className="text-lg font-bold">View a player&apos;s portal</h1>
        <p className="mt-1 text-sm" style={{ color: theme.textMuted }}>
          Search players in the rooms you manage. You&apos;ll see their portal exactly as they do,
          in read-only mode.
        </p>

        <div className="relative mt-5">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2" style={{ color: theme.textMuted }} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Start typing a name or card ID…"
            autoFocus
            className="w-full rounded-lg py-2.5 pl-10 pr-3 text-sm outline-none"
            style={{ background: theme.panelLight, color: theme.text, border: `1px solid ${theme.border}` }}
          />
        </div>

        <div className="mt-4 max-h-80 overflow-auto">
          {search.isLoading ? (
            <p className="py-6 text-center text-sm" style={{ color: theme.textMuted }}>
              Searching…
            </p>
          ) : search.isError ? (
            <p className="py-6 text-center text-sm" style={{ color: theme.textMuted }}>
              Couldn&apos;t search players.
            </p>
          ) : results.length === 0 ? (
            <p className="py-6 text-center text-sm" style={{ color: theme.textMuted }}>
              {query
                ? 'No players match that search.'
                : 'No players in the rooms you manage yet.'}
            </p>
          ) : (
            <ul className="space-y-1">
              {results.map((player) => (
                <li key={player.id}>
                  <button
                    type="button"
                    onClick={() => {
                      selectPlayer(player.id);
                      onClose?.();
                    }}
                    className="flex w-full items-center gap-3 rounded-lg p-3 text-left transition hover:opacity-90"
                    style={{ background: theme.panelLight }}
                  >
                    <span
                      className="flex size-9 shrink-0 items-center justify-center rounded-full text-xs font-bold"
                      style={{ background: theme.accent, color: theme.bg }}
                    >
                      {player.avatar}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold">{player.name}</span>
                      <span className="block text-xs" style={{ color: theme.textMuted }}>
                        {pickerSubtitle(player, rooms)}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <p className="mt-4 text-[11px]" style={{ color: theme.textMuted }}>
          Showing the first matches only — refine the search to narrow it.
        </p>

        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            className="mt-4 w-full rounded-lg py-2 text-sm"
            style={{ border: `1px solid ${theme.border}`, color: theme.textMuted }}
          >
            Cancel
          </button>
        ) : null}

        {/* Staff browse and players read their own card, and the role in force is what decides
            which (player.tsx) — so this screen is now the front of a fork rather than the end of
            one, and the way to the other branch has to be on it. */}
        <RoleSwitch />
      </div>
    </CenteredScreen>
  );
}
